import { useState, useMemo } from "react";
import { BarChart3, TrendingUp, TrendingDown, Layers } from "lucide-react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  type TooltipItem,
} from "chart.js";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { t } from "@/lib/i18n/messages";
import { formatCurrency } from "@/lib/utils/format";
import { aggregateVolume } from "@/lib/utils/aggregate";
import { useDashboardPeriod } from "@/context/dashboard-period-context";
import { useAppContext } from "@/providers";
import type { Locale } from "@/types/auth";
import type { Transaction } from "@/types/wallet";
import { cn } from "@/lib/utils";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface TransactionVolumeChartProps {
  locale: Locale;
  transactions: Transaction[];
  loading: boolean;
  className?: string;
}

type ViewMode = "amount" | "count";

export function TransactionVolumeChart({
  locale,
  transactions,
  loading,
  className,
}: TransactionVolumeChartProps) {
  const { period } = useDashboardPeriod();
  const { theme } = useAppContext();
  const isDark = theme === "dark";
  const isRTL = locale === "ar";

  // Toggle between displaying amounts in NC vs transaction count
  const [viewMode, setViewMode] = useState<ViewMode>("amount");

  // custom period uses monthly aggregation for display
  const aggPeriod = period === "custom" || period === "monthly" ? "monthly" : period;

  const aggregated = useMemo(
    () => aggregateVolume(transactions, aggPeriod as "daily" | "weekly" | "monthly"),
    [transactions, aggPeriod]
  );

  const { totalInboundAmount, totalOutboundAmount, totalInboundCount, totalOutboundCount, hasData } =
    useMemo(() => {
      const inAmt = aggregated.reduce((s, p) => s + (p.inboundTotal || 0), 0);
      const outAmt = aggregated.reduce((s, p) => s + (p.outboundTotal || 0), 0);
      const inCnt = aggregated.reduce((s, p) => s + (p.inboundCount || 0), 0);
      const outCnt = aggregated.reduce((s, p) => s + (p.outboundCount || 0), 0);

      return {
        totalInboundAmount: inAmt,
        totalOutboundAmount: outAmt,
        totalInboundCount: inCnt,
        totalOutboundCount: outCnt,
        hasData: inCnt > 0 || outCnt > 0 || inAmt > 0 || outAmt > 0,
      };
    }, [aggregated]);

  if (loading) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-border bg-card p-5 md:p-7 space-y-4 shadow-xs h-full",
          className
        )}
        aria-hidden="true"
      >
        <Skeleton className="h-5 w-40 rounded-lg" />
        <div className="flex items-end gap-2 h-56 pt-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 skeleton rounded-lg animate-pulse"
              style={{ height: `${35 + i * 12}%` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs h-full flex flex-col justify-between",
          className
        )}
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
            <BarChart3 className="w-4 h-4" />
          </span>
          <h3 className="text-base md:text-lg font-bold text-foreground">
            {t(locale, "transactionVolume")}
          </h3>
        </div>
        <EmptyState
          icon={BarChart3}
          title={t(locale, "noTransactions")}
          description={t(locale, "noDataAvailable")}
          className="border-0 py-10"
        />
      </div>
    );
  }

  const chartData = {
    labels: aggregated.map((p) => p.label),
    datasets: [
      {
        label: t(locale, "inbound"),
        data: aggregated.map((p) =>
          viewMode === "amount" ? p.inboundTotal : p.inboundCount
        ),
        backgroundColor: "#10B981", // Emerald 500
        borderRadius: 6,
        borderSkipped: false,
      },
      {
        label: t(locale, "outbound"),
        data: aggregated.map((p) =>
          viewMode === "amount" ? p.outboundTotal : p.outboundCount
        ),
        backgroundColor: "#F43F5E", // Rose 500
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: "top" as const,
        align: (isRTL ? "start" : "end") as "start" | "end",
        rtl: isRTL,
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          usePointStyle: true,
          pointStyle: "circle",
          padding: 14,
          color: isDark ? "#CBD5E1" : "#334155",
          font: { size: 12, family: "inherit" },
        },
      },
      tooltip: {
        rtl: isRTL,
        backgroundColor: isDark ? "rgba(15, 23, 42, 0.95)" : "rgba(255, 255, 255, 0.95)",
        titleColor: isDark ? "#F8FAFC" : "#0F172A",
        bodyColor: isDark ? "#CBD5E1" : "#334155",
        borderColor: isDark ? "#334155" : "#E2E8F0",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 10,
        callbacks: {
          label: (ctx: TooltipItem<"bar">) => {
            const ds = ctx.dataset.label ?? "";
            const entry = aggregated[ctx.dataIndex];
            const isInbound = ds === t(locale, "inbound");
            const amt = isInbound ? entry?.inboundTotal ?? 0 : entry?.outboundTotal ?? 0;
            const cnt = isInbound ? entry?.inboundCount ?? 0 : entry?.outboundCount ?? 0;

            if (viewMode === "amount") {
              return ` ${ds}: ${formatCurrency(amt, locale)} (${cnt} ${isRTL ? "عملية" : "txs"})`;
            }
            return ` ${ds}: ${cnt} (${formatCurrency(amt, locale)})`;
          },
        },
      },
    },
    scales: {
      x: {
        reverse: isRTL,
        grid: { display: false },
        ticks: {
          color: isDark ? "#94A3B8" : "#64748B",
          font: { size: 11, family: "inherit" },
        },
      },
      y: {
        beginAtZero: true,
        position: (isRTL ? "right" : "left") as "right" | "left",
        grid: {
          color: isDark ? "rgba(51, 65, 85, 0.4)" : "rgba(226, 232, 240, 0.8)",
        },
        ticks: {
          color: isDark ? "#94A3B8" : "#64748B",
          font: { size: 11, family: "inherit" },
          callback: (value: any) =>
            viewMode === "amount"
              ? formatCurrency(value, locale)
              : Number(value).toLocaleString(),
        },
      },
    },
  };

  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-card p-5 md:p-7 shadow-xs space-y-5 h-full flex flex-col justify-between",
        className
      )}
    >
      {/* ─── Header & Mode Switcher ───────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
              <BarChart3 className="w-4 h-4" />
            </span>
            <h3 className="text-base md:text-lg font-bold text-foreground">
              {t(locale, "transactionVolume")}
            </h3>
          </div>
          <p className="text-xs text-foreground-secondary mt-1">
            {isRTL
              ? "مقارنة حجم المبالغ الواردة والصادرة عبر الزمن."
              : "Comparison of inbound vs outbound transaction volumes over time."}
          </p>
        </div>

        {/* View Mode Toggle: Amount (NC) vs Count */}
        <div
          className="inline-flex items-center p-1 rounded-xl bg-muted/60 border border-border self-start sm:self-auto"
          role="tablist"
        >
          <button
            type="button"
            onClick={() => setViewMode("amount")}
            className={cn(
              "px-3 py-1 rounded-lg text-xs font-semibold transition-all",
              viewMode === "amount"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-foreground-secondary hover:text-foreground"
            )}
          >
            {isRTL ? "المبالغ (NC)" : "Amounts (NC)"}
          </button>
          <button
            type="button"
            onClick={() => setViewMode("count")}
            className={cn(
              "px-3 py-1 rounded-lg text-xs font-semibold transition-all",
              viewMode === "count"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-foreground-secondary hover:text-foreground"
            )}
          >
            {isRTL ? "عدد العمليات" : "Count"}
          </button>
        </div>
      </div>

      {/* ─── Always-Visible Totals Badges ─────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 pt-1">
        {/* Inbound Total */}
        <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 block">
              {isRTL ? "إجمالي الوارد" : "Total Inbound"}
            </span>
            <span className="text-base sm:text-lg font-bold text-foreground">
              {formatCurrency(totalInboundAmount, locale)}
            </span>
          </div>
          <div className="text-end">
            <span className="text-xs text-foreground-secondary block">
              {totalInboundCount} {isRTL ? "عملية" : "txs"}
            </span>
            <div className="w-5 h-5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 inline-flex items-center justify-center mt-0.5">
              <TrendingUp className="w-3 h-3" />
            </div>
          </div>
        </div>

        {/* Outbound Total */}
        <div className="p-3 rounded-xl bg-rose-500/5 border border-rose-500/20 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-medium text-rose-600 dark:text-rose-400 block">
              {isRTL ? "إجمالي الصادر" : "Total Outbound"}
            </span>
            <span className="text-base sm:text-lg font-bold text-foreground">
              {formatCurrency(totalOutboundAmount, locale)}
            </span>
          </div>
          <div className="text-end">
            <span className="text-xs text-foreground-secondary block">
              {totalOutboundCount} {isRTL ? "عملية" : "txs"}
            </span>
            <div className="w-5 h-5 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 inline-flex items-center justify-center mt-0.5">
              <TrendingDown className="w-3 h-3" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── Bar Chart with Visible Amounts ──────────────────────────── */}
      <div className="h-64 sm:h-72 w-full pt-1">
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}
